/**
 * NotificationCenter SignalR客户端
 * 基于@microsoft/signalr实现的通知中心客户端
 */
import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr'

export class SignalRClient {
  /**
   * 构造函数
   * @param {Object} options - 选项
   * @param {boolean} options.autoReconnect - 是否自动重连
   * @param {number} options.maxReconnectAttempts - 最大重连尝试次数
   * @param {number} options.reconnectInterval - 重连间隔时间(秒)
   */
  constructor(options = {}) {
    console.log('初始化SignalR客户端', options);
    
    this._connection = null
    this._connectionId = null
    this._clientId = this._generateGuid()
    this._serverUrl = null
    this._isConnected = false
    this._messageHandlers = []
    this._connectionStatusHandlers = []
    
    // 增强特性
    this._autoReconnect = options.autoReconnect !== false
    this._maxReconnectAttempts = options.maxReconnectAttempts || 999999
    this._reconnectInterval = (options.reconnectInterval || 3) * 1000
    this._activeSubscriptions = new Set()
    this._activeGroups = new Set()
    
    // 初始化状态
    this._reconnectAttempts = 0
    this._reconnectTimer = null
    this._pendingOperations = new Map() // 存储待处理的操作
    
    console.log(`SignalR客户端初始化完成, 客户端ID: ${this._clientId}`);
    console.log('SignalR配置:', {
      autoReconnect: this._autoReconnect,
      maxReconnectAttempts: this._maxReconnectAttempts,
      reconnectInterval: this._reconnectInterval / 1000 + '秒'
    });
  }

  /**
   * 获取连接ID
   * @return {string|null} 连接ID
   */
  get connectionId() {
    return this._connectionId
  }

  /**
   * 获取客户端ID
   * @return {string} 客户端ID
   */
  get clientId() {
    return this._clientId
  }

  /**
   * 设置客户端ID（在连接前调用以更新连接查询参数中的clientId）
   * @param {string} clientId - 新的客户端ID
   */
  setClientId(clientId) {
    if (!clientId) {
      console.warn('setClientId被调用但clientId为空，忽略');
      return;
    }
    this._clientId = clientId.toString();
    console.log('SignalR客户端ID已更新为:', this._clientId);
  }

  /**
   * 获取连接状态
   * @return {boolean} 是否已连接
   */
  get isConnected() {
    return this._isConnected && this._connection && 
      this._connection.state === HubConnectionState.Connected
  }

  /**
   * 生成GUID
   * @private
   */
  _generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  /**
   * 连接到服务器
   * @param {string} url - 服务器URL
   * @param {Object} queryParams - 查询参数
   * @param {string} queryParams.userId - 用户ID
   * @param {string} queryParams.deviceId - 设备ID
   * @param {string} queryParams.customType - 自定义类型
   * @return {Promise} 连接Promise
   */
  async connect(url, queryParams = {}) {
    if (this.isConnected) {
      console.log('已经连接，跳过连接过程')
      return
    }

    // 确保URL处理正确
    let serverUrl = url;
    
    // 移除尾部斜杠
    if (serverUrl.endsWith('/')) {
      serverUrl = serverUrl.slice(0, -1);
    }
    
    // 检查URL是否包含hub路径
    const urlLower = serverUrl.toLowerCase();
    const hasHub = urlLower.includes('hub');
    const hasNotification = urlLower.includes('notification');
    
    console.log(`URL分析: 包含hub=${hasHub}, 包含notification=${hasNotification}`);
    
    // 处理不同情况
    if (!hasHub) {
      console.warn('URL可能不包含hub路径，尝试添加');
      
      if (hasNotification) {
        // 已有notification但没有hub，可能是类似 http://localhost:5000/notification
        if (urlLower.endsWith('notification')) {
          serverUrl = `${serverUrl}Hub`;
          console.log(`添加Hub后缀: ${serverUrl}`);
        } else {
          // 其他情况，可能是类似 http://localhost:5000/notification-api
          serverUrl = `${serverUrl}/notification-hub`;
          console.log(`添加完整hub路径: ${serverUrl}`);
        }
      } else {
        // 既没有notification也没有hub
        serverUrl = `${serverUrl}/notification-hub`;
        console.log(`添加默认hub路径: ${serverUrl}`);
      }
    }
    
    console.log(`最终连接URL: ${serverUrl}`);
    this._serverUrl = serverUrl;
    
    try {
      // 构建查询字符串
      const query = new URLSearchParams({
        clientId: this._clientId,
        ...queryParams
      }).toString()
      
      console.log(`完整连接URL: ${serverUrl}?${query}`);
      
      // 构建连接
      this._connection = new HubConnectionBuilder()
        .withUrl(`${serverUrl}?${query}`, {
          withCredentials: false, // 不使用凭证模式，避免CORS问题
          skipNegotiation: false, // 不跳过协商
          // 明确指定传输类型，按优先级尝试
          transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            // 自定义重试逻辑
            if (retryContext.previousRetryCount >= this._maxReconnectAttempts) {
              return null // 停止重试
            }
            
            // 指数退避策略
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
          }
        })
        .configureLogging(LogLevel.Debug) // 使用Debug级别获取更多日志
        .build()
        
      // 注册事件处理
      this._registerHubEvents()
      
      // 启动连接
      console.log('正在启动连接...');
      await this._connection.start()
      this._connectionId = this._connection.connectionId
      this._isConnected = true
      
      console.log(`连接成功，连接ID: ${this._connectionId}`);
      
      // 通知连接状态变更
      this._notifyConnectionStatus(true, this._connectionId, '已连接')
      
      // 恢复订阅和组成员身份
      await this._restoreSubscriptionsAndGroups()
      
      return this._connectionId
    } catch (error) {
      this._isConnected = false
      console.error('连接失败:', error)
      console.error('连接错误详情:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      this._notifyConnectionStatus(false, null, `连接失败: ${error.message}`)
      throw error
    }
  }

  /**
   * 注册Hub事件
   * @private
   */
  _registerHubEvents() {
    if (!this._connection) return
    
    // 接收消息事件
    this._connection.on('ReceiveMessage', message => {
      //console.log('收到消息:', message);
      this._messageReceivedCallback(message)
    })
    
    // 发送消息结果事件 - 使用驼峰命名法匹配服务端
    this._connection.on('SendMessageResult', result => {
      console.info('发送消息结果:', result)
    })
    
    // 同时监听Pascal命名和驼峰命名的事件
    
    // 订阅相关事件
    this._connection.on('SubscribeSuccess', result => {
      console.info('订阅成功(Pascal):', result)
      this._handleSubscribeSuccess(result)
    })
    
    this._connection.on('subscribeSuccess', result => {
      console.info('订阅成功(camel):', result)
      this._handleSubscribeSuccess(result)
    })
    
    this._connection.on('SubscribeFailed', result => {
      console.error('订阅失败事件(Pascal):', result)
      this._handleSubscribeFailed(result)
    })
    
    this._connection.on('subscribeFailed', result => {
      console.error('订阅失败事件(camel):', result)
      this._handleSubscribeFailed(result)
    })
    
    // 取消订阅事件
    this._connection.on('UnsubscribeSuccess', result => {
      console.info('取消订阅成功(Pascal):', result)
      this._handleUnsubscribeSuccess(result)
    })
    
    this._connection.on('unsubscribeSuccess', result => {
      console.info('取消订阅成功(camel):', result)
      this._handleUnsubscribeSuccess(result)
    })
    
    this._connection.on('UnsubscribeFailed', result => {
      console.error('取消订阅失败事件(Pascal):', result)
      this._handleUnsubscribeFailed(result)
    })
    
    this._connection.on('unsubscribeFailed', result => {
      console.error('取消订阅失败事件(camel):', result)
      this._handleUnsubscribeFailed(result)
    })
    
    // 组相关事件
    this._connection.on('JoinGroupSuccess', result => {
      console.info('加入组成功(Pascal):', result)
      this._handleJoinGroupSuccess(result)
    })
    
    this._connection.on('joinGroupSuccess', result => {
      console.info('加入组成功(camel):', result)
      this._handleJoinGroupSuccess(result)
    })
    
    this._connection.on('JoinGroupFailed', result => {
      console.error('加入组失败事件(Pascal):', result)
      this._handleJoinGroupFailed(result)
    })
    
    this._connection.on('joinGroupFailed', result => {
      console.error('加入组失败事件(camel):', result)
      this._handleJoinGroupFailed(result)
    })
    
    this._connection.on('LeaveGroupSuccess', result => {
      console.info('离开组成功(Pascal):', result)
      this._handleLeaveGroupSuccess(result)
    })
    
    this._connection.on('leaveGroupSuccess', result => {
      console.info('离开组成功(camel):', result)
      this._handleLeaveGroupSuccess(result)
    })
    
    this._connection.on('LeaveGroupFailed', result => {
      console.error('离开组失败事件(Pascal):', result)
      this._handleLeaveGroupFailed(result)
    })
    
    this._connection.on('leaveGroupFailed', result => {
      console.error('离开组失败事件(camel):', result)
      this._handleLeaveGroupFailed(result)
    })
    
    // 身份注册相关事件
    this._connection.on('RegisterIdentityResult', result => {
      console.info('身份注册结果(Pascal):', result)
      this._handleRegisterIdentityResult(result)
    })
    
    this._connection.on('registerIdentityResult', result => {
      console.info('身份注册结果(camel):', result)
      this._handleRegisterIdentityResult(result)
    })
    
    // 重连事件
    this._connection.onreconnecting(error => {
      this._isConnected = false
      console.warn('正在重新连接:', error)
      this._notifyConnectionStatus(false, this._connectionId, `正在重新连接: ${error?.message || '连接断开'}`)
    })
    
    this._connection.onreconnected(connectionId => {
      this._connectionId = connectionId
      this._isConnected = true
      console.info('重连成功, 新连接ID:', connectionId)
      this._notifyConnectionStatus(true, connectionId, '重连成功')
      
      // 重连后重新注册身份信息
      this._registerIdentityFromStorage()
      
      // 恢复订阅和组成员身份
      this._restoreSubscriptionsAndGroups()
    })
    
    this._connection.onclose(error => {
      this._isConnected = false
      console.warn('连接关闭:', error)
      this._notifyConnectionStatus(false, null, `连接关闭: ${error?.message || '连接已关闭'}`)
      
      // 如果需要自动重连，但内置重连失败或不可用时
      if (this._autoReconnect && !this._connection.reconnect) {
        this._startManualReconnect()
      }
    })
  }

  /**
   * 处理订阅成功事件
   * @private
   */
  _handleSubscribeSuccess(result) {
    const topic = result?.Topic || result?.topic
    if (!topic) return
    
    const pendingKey = `subscribe:${topic}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.resolve(result)
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理订阅失败事件
   * @private
   */
  _handleSubscribeFailed(result) {
    const topic = result?.Topic || result?.topic
    if (!topic) return
    
    const pendingKey = `subscribe:${topic}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      this._activeSubscriptions.delete(topic)
      pendingOp.reject(new Error(result?.Error || result?.error || '订阅失败'))
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理取消订阅成功事件
   * @private
   */
  _handleUnsubscribeSuccess(result) {
    const topic = result?.Topic || result?.topic
    if (!topic) return
    
    const pendingKey = `unsubscribe:${topic}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.resolve(result)
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理取消订阅失败事件
   * @private
   */
  _handleUnsubscribeFailed(result) {
    const topic = result?.Topic || result?.topic
    if (!topic) return
    
    const pendingKey = `unsubscribe:${topic}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.reject(new Error(result?.Error || result?.error || '取消订阅失败'))
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理加入组成功事件
   * @private
   */
  _handleJoinGroupSuccess(result) {
    const group = result?.GroupName || result?.groupName
    if (!group) return
    
    const pendingKey = `joinGroup:${group}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.resolve(result)
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理加入组失败事件
   * @private
   */
  _handleJoinGroupFailed(result) {
    const group = result?.GroupName || result?.groupName
    if (!group) return
    
    const pendingKey = `joinGroup:${group}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      this._activeGroups.delete(group)
      pendingOp.reject(new Error(result?.Error || result?.error || '加入组失败'))
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理离开组成功事件
   * @private
   */
  _handleLeaveGroupSuccess(result) {
    const group = result?.GroupName || result?.groupName
    if (!group) return
    
    const pendingKey = `leaveGroup:${group}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.resolve(result)
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理离开组失败事件
   * @private
   */
  _handleLeaveGroupFailed(result) {
    const group = result?.GroupName || result?.groupName
    if (!group) return
    
    const pendingKey = `leaveGroup:${group}`
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      pendingOp.reject(new Error(result?.Error || result?.error || '离开组失败'))
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 处理身份注册结果事件
   * @private
   */
  _handleRegisterIdentityResult(result) {
    const pendingKey = 'registerIdentity'
    const pendingOp = this._pendingOperations.get(pendingKey)
    
    if (pendingOp) {
      clearTimeout(pendingOp.timeout)
      if (result && (result.Success || result.success)) {
        console.info('身份注册成功:', result.Identity || result.identity)
        pendingOp.resolve(result)
      } else {
        console.error('身份注册失败:', result?.Message || result?.message)
        pendingOp.reject(new Error(result?.Message || result?.message || '身份注册失败'))
      }
      this._pendingOperations.delete(pendingKey)
    }
  }

  /**
   * 恢复订阅和组成员身份
   * @private
   */
  async _restoreSubscriptionsAndGroups() {
    // 复制集合避免迭代过程中修改
    const subscriptions = Array.from(this._activeSubscriptions)
    const groups = Array.from(this._activeGroups)
    
    // 先清空集合，因为在恢复过程中会重新添加
    this._activeSubscriptions.clear()
    this._activeGroups.clear()
    
    // 恢复订阅
    for (const topic of subscriptions) {
      try {
        console.info(`恢复订阅主题: ${topic}`)
        await this.subscribe(topic)
      } catch (error) {
        console.error(`恢复订阅主题 ${topic} 失败:`, error)
      }
    }
    
    // 恢复组成员身份
    for (const group of groups) {
      try {
        console.info(`恢复加入组: ${group}`)
        await this.joinGroup(group)
      } catch (error) {
        console.error(`恢复加入组 ${group} 失败:`, error)
      }
    }
  }

  /**
   * 手动启动重连
   * @private
   */
  _startManualReconnect() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
    }
    
    this._reconnectAttempts = 0
    this._tryReconnect()
  }

  /**
   * 尝试重连
   * @private
   */
  _tryReconnect() {
    if (this._isConnected || this._reconnectAttempts >= this._maxReconnectAttempts) {
      return
    }
    
    this._reconnectAttempts++
    console.info(`尝试重新连接 (尝试 ${this._reconnectAttempts}/${this._maxReconnectAttempts})...`)
    
    this._reconnectTimer = setTimeout(async () => {
      try {
        // 尝试连接
        await this.connect(this._serverUrl)
        console.info('手动重连成功')
        this._reconnectAttempts = 0
      } catch (error) {
        console.error('重连失败:', error)
        this._tryReconnect()
      }
    }, this._reconnectInterval)
  }

  /**
   * 断开连接
   * @return {Promise} 断开连接Promise
   */
  async disconnect() {
    if (!this.isConnected) {
      return
    }
    
    // 清除重连定时器
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
    
    try {
      if (this._connection) {
        await this._connection.stop()
      }
    } catch (error) {
      console.error('断开连接时出错:', error)
    } finally {
      this._isConnected = false
      this._notifyConnectionStatus(false, null, '已断开连接')
    }
  }

  /**
   * 发送消息
   * @param {Object} message - 消息对象
   * @return {Promise} 发送结果Promise
   */
  async sendMessage(message) {
    if (!this.isConnected) {
      throw new Error('客户端未连接')
    }
    
    try {
      // 创建Promise以等待发送结果
      return new Promise((resolve, reject) => {
        // 一次性事件处理器
        const resultHandler = (result) => {
          this._connection.off('SendMessageResult', resultHandler)
          
          if (result && result.Success) {
            resolve(result)
          } else {
            reject(new Error(result?.Message || '发送消息失败'))
          }
        }
        
        // 添加临时事件处理器
        this._connection.on('SendMessageResult', resultHandler)
        
        // 设置超时
        const timeout = setTimeout(() => {
          this._connection.off('SendMessageResult', resultHandler)
          reject(new Error('发送消息超时'))
        }, 10000) // 10秒超时
        
        // 调用SendMessage方法
        this._connection.invoke('SendMessage', message)
          .catch(error => {
            clearTimeout(timeout)
            this._connection.off('SendMessageResult', resultHandler)
            reject(error)
          })
      })
    } catch (error) {
      console.error('发送消息失败:', error)
      throw error
    }
  }

  /**
   * 订阅主题
   * @param {string} topic - 主题名
   * @return {Promise} 订阅结果Promise
   */
  async subscribe(topic) {
    if (!this.isConnected) {
      console.error('订阅失败: 客户端未连接');
      throw new Error('客户端未连接')
    }
    
    // 确保topic是字符串类型
    if (typeof topic !== 'string') {
      console.error('订阅失败: 主题必须是字符串');
      throw new Error('主题必须是字符串')
    }
    
    // 检查topic是否为空
    if (!topic || topic.trim() === '') {
      console.error('订阅失败: 主题不能为空');
      throw new Error('主题不能为空')
    }
    
    console.log(`尝试订阅主题: ${topic}, 连接ID: ${this._connectionId}, 连接状态: ${this._connection.state}`);
    
    try {
      // 检查连接状态
      if (this._connection.state !== HubConnectionState.Connected) {
        console.error(`订阅失败: 连接状态不是Connected, 当前状态: ${this._connection.state}`);
        throw new Error(`连接状态错误: ${this._connection.state}`);
      }
      
      // 记录活动订阅
      this._activeSubscriptions.add(topic)
      
      // 创建Promise以等待订阅结果
      return new Promise((resolve, reject) => {
        const pendingKey = `subscribe:${topic}`
        
        // 设置超时
        const timeout = setTimeout(() => {
          console.error(`订阅主题超时: ${topic}`);
          this._activeSubscriptions.delete(topic)
          this._pendingOperations.delete(pendingKey)
          reject(new Error('订阅超时'))
        }, 15000) // 15秒超时
        
        // 存储待处理操作
        this._pendingOperations.set(pendingKey, {
          resolve,
          reject,
          timeout
        })
        
        // 调用Subscribe方法
        console.log(`调用Subscribe方法, 主题: ${topic}`);
        this._connection.invoke('Subscribe', topic)
          .then(() => {
            console.log(`Subscribe方法调用成功, 主题: ${topic}, 等待服务器响应...`);
          })
          .catch(error => {
            console.error(`Subscribe方法调用失败, 主题: ${topic}, 错误:`, error);
            console.error('详细错误信息:', {
              message: error.message,
              stack: error.stack,
              name: error.name,
              hubConnection: this._connection ? {
                state: this._connection.state,
                connectionId: this._connection.connectionId
              } : 'connection is null'
            });
            
            clearTimeout(timeout)
            this._pendingOperations.delete(pendingKey)
            this._activeSubscriptions.delete(topic)
            
            // 检查特定错误类型
            if (error.message && error.message.includes('server')) {
              reject(new Error(`浏览器网络有波动，请稍后再试: ${error.message}`))
            } else {
              reject(error)
            }
          })
      })
    } catch (error) {
      this._activeSubscriptions.delete(topic)
      console.error(`订阅主题出错: ${topic}`, error);
      console.error('订阅错误详情:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error
    }
  }

  /**
   * 取消订阅主题
   * @param {string} topic - 主题名
   * @return {Promise} 取消订阅结果Promise
   */
  async unsubscribe(topic) {
    if (!this.isConnected) {
      throw new Error('客户端未连接')
    }
    
    try {
      // 从活动订阅中移除
      this._activeSubscriptions.delete(topic)
      
      // 创建Promise以等待取消订阅结果
      return new Promise((resolve, reject) => {
        const pendingKey = `unsubscribe:${topic}`
        
        // 设置超时
        const timeout = setTimeout(() => {
          this._pendingOperations.delete(pendingKey)
          reject(new Error('取消订阅超时'))
        }, 15000) // 15秒超时
        
        // 存储待处理操作
        this._pendingOperations.set(pendingKey, {
          resolve,
          reject,
          timeout
        })
        
        // 调用Unsubscribe方法
        this._connection.invoke('Unsubscribe', topic)
          .catch(error => {
            clearTimeout(timeout)
            this._pendingOperations.delete(pendingKey)
            reject(error)
          })
      })
    } catch (error) {
      console.error('取消订阅失败:', error)
      throw error
    }
  }

  /**
   * 加入组
   * @param {string} groupName - 组名
   * @return {Promise} 加入组结果Promise
   */
  async joinGroup(groupName) {
    if (!this.isConnected) {
      throw new Error('客户端未连接')
    }
    
    try {
      // 记录活动组
      this._activeGroups.add(groupName)
      
      // 创建Promise以等待加入组结果
      return new Promise((resolve, reject) => {
        const pendingKey = `joinGroup:${groupName}`
        
        // 设置超时
        const timeout = setTimeout(() => {
          this._pendingOperations.delete(pendingKey)
          this._activeGroups.delete(groupName)
          reject(new Error('加入组超时'))
        }, 15000) // 15秒超时
        
        // 存储待处理操作
        this._pendingOperations.set(pendingKey, {
          resolve,
          reject,
          timeout
        })
        
        // 调用JoinGroup方法
        this._connection.invoke('JoinGroup', groupName)
          .catch(error => {
            clearTimeout(timeout)
            this._pendingOperations.delete(pendingKey)
            this._activeGroups.delete(groupName)
            reject(error)
          })
      })
    } catch (error) {
      this._activeGroups.delete(groupName)
      console.error('加入组失败:', error)
      throw error
    }
  }

  /**
   * 离开组
   * @param {string} groupName - 组名
   * @return {Promise} 离开组结果Promise
   */
  async leaveGroup(groupName) {
    if (!this.isConnected) {
      throw new Error('客户端未连接')
    }
    
    try {
      // 从活动组中移除
      this._activeGroups.delete(groupName)
      
      // 创建Promise以等待离开组结果
      return new Promise((resolve, reject) => {
        const pendingKey = `leaveGroup:${groupName}`
        
        // 设置超时
        const timeout = setTimeout(() => {
          this._pendingOperations.delete(pendingKey)
          reject(new Error('离开组超时'))
        }, 15000) // 15秒超时
        
        // 存储待处理操作
        this._pendingOperations.set(pendingKey, {
          resolve,
          reject,
          timeout
        })
        
        // 调用LeaveGroup方法
        this._connection.invoke('LeaveGroup', groupName)
          .catch(error => {
            clearTimeout(timeout)
            this._pendingOperations.delete(pendingKey)
            reject(error)
          })
      })
    } catch (error) {
      console.error('离开组失败:', error)
      throw error
    }
  }

  /**
   * 添加消息接收处理器
   * @param {Function} handler - 处理函数，接收Message参数
   */
  addMessageReceivedHandler(handler) {
    this._messageHandlers.push(handler)
  }

  /**
   * 添加连接状态变更处理器
   * @param {Function} handler - 处理函数，接收is_connected, connection_id, detail参数
   */
  addConnectionStatusChangedHandler(handler) {
    this._connectionStatusHandlers.push(handler)
  }

  /**
   * 关闭客户端并释放资源
   */
  close() {
    this.disconnect()
    this._messageHandlers = []
    this._connectionStatusHandlers = []
    this._activeSubscriptions.clear()
    this._activeGroups.clear()
    this._connection = null
    this._connectionId = null
    this._serverUrl = null
    this._isConnected = false
  }

  /**
   * 消息接收回调函数
   * @param {Object} message - 接收到的消息
   * @private
   */
  _messageReceivedCallback(message) {
    for (const handler of this._messageHandlers) {
      try {
        handler(message)
      } catch (error) {
        console.error('消息处理器出错:', error)
      }
    }
  }

  /**
   * 通知连接状态变更
   * @param {boolean} isConnected - 是否已连接
   * @param {string|null} connectionId - 连接ID
   * @param {string|null} detail - 详细信息
   * @private
   */
  _notifyConnectionStatus(isConnected, connectionId, detail) {
    for (const handler of this._connectionStatusHandlers) {
      try {
        handler(isConnected, connectionId, detail)
      } catch (error) {
        console.error('连接状态处理器出错:', error)
      }
    }
  }

  /**
   * 向主题发送消息
   * @param {string} topic - 主题名称
   * @param {string} messageType - 消息类型
   * @param {string} payload - 消息内容
   * @param {Object} metadata - 元数据
   */
  async sendToTopic(topic, messageType, payload, metadata = {}) {
    return await this.sendCustomMessage({
      targetTopic: topic,
      messageType,
      payload,
      metadata
    })
  }

  /**
   * 向组发送消息
   * @param {string} group - 组名称
   * @param {string} messageType - 消息类型
   * @param {string} payload - 消息内容
   * @param {Object} metadata - 元数据
   */
  async sendToGroup(group, messageType, payload, metadata = {}) {
    return await this.sendCustomMessage({
      targetGroup: group,
      messageType,
      payload,
      metadata
    })
  }

  /**
   * 向客户端发送消息
   * @param {string} clientId - 客户端ID
   * @param {string} messageType - 消息类型
   * @param {string} payload - 消息内容
   * @param {Object} metadata - 元数据
   */
  async sendToClient(clientId, messageType, payload, metadata = {}) {
    return await this.sendCustomMessage({
      targetClientId: clientId,
      messageType,
      payload,
      metadata
    })
  }

  /**
   * 从localStorage读取身份信息并注册
   * @private
   */
  async _registerIdentityFromStorage() {
    try {
      const identityInfo = localStorage.getItem('signalrClientInfo')
      if (!identityInfo) {
        console.log('localStorage中没有找到signalrClientInfo，跳过身份注册')
        return
      }

      let identityData
      try {
        identityData = JSON.parse(identityInfo)
      } catch (parseError) {
        console.error('解析signalrClientInfo失败:', parseError)
        return
      }

      if (!identityData || (!identityData.userId && !identityData.deviceId && !identityData.customType)) {
        console.log('signalrClientInfo中没有有效的身份信息，跳过身份注册')
        return
      }

      console.log('从localStorage读取到身份信息:', identityData)

      // 如果存储中有clientId（用于桥梁ID），同步到客户端实例
      if (identityData.clientId && typeof this.setClientId === 'function') {
        this.setClientId(identityData.clientId)
      }

      // 确保metaData中的数值类型也转换为字符串
      const normalizedMetaData = identityData.metaData ? { ...identityData.metaData } : {}
      if (normalizedMetaData.bridgeId != null) {
        normalizedMetaData.bridgeId = String(normalizedMetaData.bridgeId)
      }

      await this.registerIdentity({
        clientId: identityData.clientId != null ? String(identityData.clientId) : '',
        userId: identityData.userId != null ? String(identityData.userId) : '',
        deviceId: identityData.deviceId != null ? String(identityData.deviceId) : '',
        customType: identityData.customType != null ? String(identityData.customType) : '',
        metaData: normalizedMetaData
      })

    } catch (error) {
      console.error('自动身份注册失败:', error)
    }
  }

  /**
   * 注册身份信息
   * @param {Object} identityDto - 身份信息
   * @param {string} identityDto.clientId - 客户端ID
   * @param {string} identityDto.userId - 用户ID
   * @param {string} identityDto.deviceId - 设备ID
   * @param {string} identityDto.customType - 自定义类型
   * @param {Object} identityDto.metaData - 元数据
   * @return {Promise} 注册结果Promise
   */
  async registerIdentity(identityDto) {
    if (!this.isConnected) {
      throw new Error('客户端未连接')
    }
    
    // 如传入clientId，同步到实例，确保后续连接参数一致
    if (identityDto && identityDto.clientId && typeof this.setClientId === 'function') {
      this.setClientId(identityDto.clientId)
    }

    try {
      // 确保所有字段都是字符串类型，避免服务端类型转换错误
      // 同时确保metaData中的bridgeId也是字符串
      const normalizedMetaData = identityDto.metaData ? { ...identityDto.metaData } : {}
      if (normalizedMetaData.bridgeId != null) {
        normalizedMetaData.bridgeId = String(normalizedMetaData.bridgeId)
      }
      
      const normalizedDto = {
        clientId: identityDto.clientId != null ? String(identityDto.clientId) : '',
        userId: identityDto.userId != null ? String(identityDto.userId) : '',
        deviceId: identityDto.deviceId != null ? String(identityDto.deviceId) : '',
        customType: identityDto.customType != null ? String(identityDto.customType) : '',
        metaData: normalizedMetaData
      }
      
      console.log('注册身份信息:', normalizedDto)
      
      return new Promise((resolve, reject) => {
        const pendingKey = 'registerIdentity'
        
        const timeout = setTimeout(() => {
          this._pendingOperations.delete(pendingKey)
          reject(new Error('身份注册超时'))
        }, 10000) // 10秒超时
        
        this._pendingOperations.set(pendingKey, {
          resolve,
          reject,
          timeout
        })
        
        this._connection.invoke('RegisterIdentity', normalizedDto)
          .catch(error => {
            clearTimeout(timeout)
            this._pendingOperations.delete(pendingKey)
            reject(error)
          })
      })
    } catch (error) {
      console.error('注册身份信息失败:', error)
      throw error
    }
  }

  /**
   * 发送自定义消息
   * @param {Object} options - 消息选项
   * @param {string} options.targetTopic - 目标主题
   * @param {string} options.targetGroup - 目标组
   * @param {string} options.targetClientId - 目标客户端ID
   * @param {string} options.targetUserId - 目标用户ID
   * @param {string} options.targetDeviceId - 目标设备ID
   * @param {string} options.targetCustomType - 目标自定义类型
   * @param {string} options.messageType - 消息类型
   * @param {string} options.payload - 消息内容
   * @param {Object} options.metadata - 元数据
   * @return {Promise} 发送结果Promise
   */
  async sendCustomMessage({ targetTopic, targetGroup, targetClientId, targetUserId, targetDeviceId, targetCustomType, messageType = 'test', payload = '', metadata = {} }) {
    // 构建消息对象
    const message = {
      Type: messageType,
      Target: {
        Topic: targetTopic,
        Group: targetGroup,
        ClientId: targetClientId,
        UserId: targetUserId,
        DeviceId: targetDeviceId,
        CustomType: targetCustomType
      },
      Payload: payload,
      MetaData: metadata
    }
    
    // 发送消息
    return await this.sendMessage(message)
  }
}

export default SignalRClient 